import { crc16 } from '../shared/crc';
import type { ValidateResult } from './types';
import { detectTier, hexToBytes } from './binary';
import { wireErrors } from './protocol';

/**
 * Fast structural validation of an EMRG hex payload — schema bytes, length,
 * tier detection, semantic field bounds/padding, and CRC16-CCITT integrity.
 * Does not produce a decoded object or certify the reported event.
 * Use this for cheap pass/fail checks before deciding whether to fully decode.
 *
 * @param hex - Hex string to validate
 * @returns `{ valid, errors, tier, schema }`. On invalid input, `errors` lists
 *          the reasons (bad schema, wrong length, CRC mismatch).
 */
export function validate(hex: string): ValidateResult {
  const errors: string[] = [];
  let tier: 0 | 1 | 2 = 0;
  let schema = 'unknown';

  let bytes: Uint8Array;
  try {
    bytes = hexToBytes(hex);
  } catch (e) {
    errors.push(`Invalid hex: ${(e as Error).message}`);
    return { valid: false, errors, tier, schema };
  }

  try {
    tier = detectTier(bytes);
    schema = 'EMRG';
  } catch (e) {
    errors.push((e as Error).message);
    return { valid: false, errors, tier, schema };
  }

  if (bytes.length >= 2) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const expected = view.getUint16(bytes.length - 2, false);
    const actual = crc16(bytes.subarray(0, bytes.length - 2));
    if (expected !== actual) errors.push('CRC mismatch');
  }

  errors.push(...wireErrors(bytes, tier));
  return { valid: errors.length === 0, errors, tier, schema };
}
